import React, { useState, useEffect, Fragment } from 'react';
import { Title, SaveButton } from 'react-admin';
import CardContent from '@material-ui/core/CardContent';
import Card from '@material-ui/core/Card';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import RestoreIcon from '@material-ui/icons/Restore';

import Mustache from 'mustache';

import './Preview.css';
import sampleVariables from './sample_variables';
import { apiGet, apiPut } from '../brainApi';

const PreviewSMS = () => {
  const [variablesString, setVariablesString] = useState(sampleVariables);
  const [cachedVariables, setCachedVariables] = useState({});
  const [variablesAreValid, setVariablesAreValid] = useState(true);
  const [template, setTemplate] = useState('');
  const [templateIsValid, setTemplateIsValid] = useState(true);

  // The purpose of "cachedTemplate" is:
  // Even if someone edits the template and uses incorrect Mustache syntax,
  // it should not break the phone display on the left.
  // That should just use the latest working template edit.
  const [cachedTemplate, setCachedTemplate] = useState('');

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [selectedTemplateID, setSelectedTemplateID] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [isOverCharacterLimit, setIsOverCharacterLimit] = useState(false);

  const checkIfVariablesAreValid = (v = variablesString) => {
    try {
      const parsed = JSON.parse(v);
      setVariablesString(v);
      setCachedVariables(parsed);
      setVariablesAreValid(true);
    } catch (e) {
      setVariablesString(v);
      setVariablesAreValid(false);
    }
  };

  const isGSMAlphabet = (text) => {
    const regexp = new RegExp(
      '^[A-Za-z0-9 \\r\\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!"#$%&\'()*+,\\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\\\\[~\\]|\u20AC]*$',
    );
    return regexp.test(text);
  };

  const updateTemplate = (t = template) => {
    try {
      const rendered = Mustache.render(t, cachedVariables);
      // Zippity uses this custom delimiter to split one text message into pieces.
      const DELIMITER = '-----';
      const splitRendered = rendered.split(DELIMITER).map((t) => t.trim());

      setTemplate(t);
      setTemplateIsValid(true);
      setCachedTemplate(t);

      // For each text, compare the length of the template the the maximum length.
      const isProperLengthArray = splitRendered.map((msg) => {
        const output = msg
          .replace(/\n/g, ' ')
          .replace(/\\n/g, '\u000A')
          .replace(/  */g, ' ')
          .replace(/\u000A /, '\u000A')
          .replace(/ \u000A/, '\u000A');
        const maxLength = isGSMAlphabet(output) ? 160 : 67;
        return output.length <= maxLength;
      });

      // Give user a warning if any of the messages have exceeded the limit
      if (isProperLengthArray.some((t) => !t)) {
        setIsOverCharacterLimit(true);
        setToastMessage('Message is over the character limit');
      } else {
        setIsOverCharacterLimit(false);
      }
    } catch (e) {
      setTemplate(t);
      setTemplateIsValid(false);
    }
  };

  const selectTemplateNameAndID = (titleToSelect) => {
    const template = templates.filter(({ title }) => title === titleToSelect);
    if (template && template.length) {
      updateTemplate(template[0].text);
      setSelectedTemplateName(titleToSelect);
      setSelectedTemplateID(template[0].filename);
    }
  };

  const loadTemplates = () => {
    apiGet('/mustache/template').then((templates) => {
      templates.sort((a, b) => {
        if (!a) {
          return b;
        }
        if (!b) {
          return a;
        }
        return a.title.localeCompare(b.title);
      });

      setTemplates(templates);

      if (templates && templates.length) {
        selectTemplateNameAndID(templates[0].title);
      }
    });
  };

  useEffect(() => {
    checkIfVariablesAreValid();
    loadTemplates();
  }, []);

  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateID) {
      // Select the first template on initial load
      selectTemplateNameAndID(templates[0].title);
    }
  }, [templates]);

  /*
  When save is clicked, update the SMS template in the database if the
  change made is valid. If the change is invalid, notify the user.
  */
  const saveTemplate = () => {
    if (templateIsValid) {
      setIsSaving(true);
      const payload = { message: template };
      apiPut(`/sms-template/${selectedTemplateID}`, payload).then(
        (response) => {
          // Find the index of the template that was just updated
          const templateIndex = templates
            .map((t) => t.filename)
            .indexOf(response.sms_template_id);
          // Transform the response into the expected template format
          const updatedTemplate = {
            filename: response.sms_template_id,
            text: response.message,
            title: response.name,
          };
          const templatesCopy = [...templates];
          // Replace the old template with the new template and save in state
          templatesCopy.splice(templateIndex, 1, updatedTemplate);
          setTemplates(templatesCopy);
          setIsSaving(false);
          setToastMessage('Template Successfully Updated');
        },
      );
    } else {
      setToastMessage('Invalid. Please manually fix changes or click Restore.');
    }
  };

  /*
  This function should restore the cached template and notify the 
  user with a toast/snackbar.
  */
  const restoreTemplate = () => {
    updateTemplate(cachedTemplate);
    setToastMessage('Template Successfully Restored');
  };

  const renderMessage = (msg) => {
    /* eslint-disable */
    // This makes sure newlines show up correctly in the phone view.
    const output = msg
      .replace(/\n/g, ' ')
      .replace(/\\n/g, '\u000A')
      .replace(/  */g, ' ')
      .replace(/\u000A /, '\u000A')
      .replace(/ \u000A/, '\u000A');
    /* eslint-enable */

    // https://www.twilio.com/docs/glossary/what-sms-character-limit
    // Character limit for GSM-7 messages is 160 characters.
    // Character limit for non-GSM-7 messages is 67 characters.
    const maxLength = isGSMAlphabet(output) ? 160 : 67;
    const prefix = output.substring(0, maxLength);
    const suffix = output.substring(maxLength);

    return (
      <Fragment key={msg}>
        <div className="phone-text">
          <span className="prefix">{prefix}</span>
          <span className="suffix">{suffix}</span>
        </div>
        <div className="phone-length">
          {output.length} / {maxLength}
        </div>
      </Fragment>
    );
  };

  const phoneScreenshot = () => {
    const rendered = Mustache.render(cachedTemplate, cachedVariables);

    // Zippity uses this custom delimiter to split one text message into pieces.
    const DELIMITER = '-----';
    const splitRendered = rendered.split(DELIMITER).map((t) => t.trim());

    return (
      <div className="phone-container">
        <img
          src="https://zippityinfra.imgix.net/iPhone-message.png?fit=max&auto=format&w=300"
          className="phone-image"
          alt="iPhone"
        />
        <div className="messages">
          {splitRendered.map((msg) => renderMessage(msg))}
        </div>
      </div>
    );
  };

  const varBorder = variablesAreValid ? '1px green solid' : '2px red solid';
  const templateBorder = templateIsValid ? '1px green solid' : '2px red solid';

  return (
    <Card style={{ height: '100%' }}>
      <Title title="resources.preview-sms.title" />
      <CardContent>
        <Grid container>
          <Grid item xs={4}>
            {phoneScreenshot()}
          </Grid>
          <Grid container item xs={8}>
            <Grid item xs={12}>
              <div className="variable-container">
                <h3 style={{ float: 'left' }}>Template</h3>

                {templates && !!templates.length && (
                  <Select
                    className="template-select"
                    value={selectedTemplateName}
                    onChange={(event) =>
                      selectTemplateNameAndID(event.target.value)
                    }
                  >
                    {templates.map(({ title }) => (
                      <MenuItem key={title} value={title}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <br />
                <textarea
                  onChange={(e) => updateTemplate(e.target.value)}
                  style={{ border: templateBorder }}
                  value={template}
                  className="template-textarea"
                />
              </div>
              <div className="variable-container">
                <h3>Variables</h3>
                <textarea
                  onChange={(e) => checkIfVariablesAreValid(e.target.value)}
                  style={{ border: varBorder }}
                  className="variables-textarea"
                  value={variablesString}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <SaveButton
                handleSubmitWithRedirect={() => saveTemplate()}
                disabled={isSaving || isOverCharacterLimit}
                saving={isSaving}
                style={{ marginLeft: '65px' }}
              />
              <Button
                variant="contained"
                onClick={() => restoreTemplate()}
                color="primary"
                startIcon={<RestoreIcon />}
                style={{ marginLeft: '5px' }}
              >
                Restore
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar
        style={{ marginLeft: '5%' }}
        message={toastMessage}
        open={!!toastMessage}
        autoHideDuration={4000}
        onClose={() => {
          setToastMessage(null);
        }}
      />
    </Card>
  );
};

export default PreviewSMS;
